/**
 * Hook for listing and controlling tmux windows in a workspace's Terminal view.
 *
 * Talks to the tmux-api HTTP shim inside the workspace pod via the
 * `/workspace/{id}/terminal-api/` nginx route, which authenticates via the same
 * `proxy-info` subrequest as the IDE/Terminal iframes.
 *
 * Auth: each request carries a fresh `?token=<access token>` from localStorage —
 * the same proven mechanism the iframe `src` and `refresh-auth` use (nginx reads
 * `$arg_token` first, before the `ws_auth` cookie). The cookie alone drifts out
 * of sync with the 5-min access-token TTL, which made this 2s poll 401-loop on a
 * stale cookie (issue #63). Using the (foreground-refreshed) localStorage token
 * keeps the poll authenticated; a transient miss just fails that tick and
 * retries — deliberately via plain fetch so the poll can NEVER trigger the api
 * client's global logout/redirect on a background 401.
 */

import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { STORAGE_KEYS } from '@utils/constants';

const TerminalWindowSchema = z.object({
  id: z.number().int().nonnegative(),
  name: z.string(),
  active: z.boolean(),
});

const WindowListSchema = z.object({
  windows: z.array(TerminalWindowSchema),
});

export type TerminalWindow = z.infer<typeof TerminalWindowSchema>;

type WindowsPayload = z.infer<typeof WindowListSchema>;

const POLL_INTERVAL_MS = 2000;

function tabsUrl(workspaceId: string, path = ''): string {
  return `/workspace/${workspaceId}/terminal-api${path}`;
}

function selectPath(id: number): string {
  return `/${String(id)}/select`;
}

function killPath(id: number): string {
  return `/${String(id)}`;
}

// Append the current access token as a query param so nginx authenticates via
// $arg_token (read fresh from localStorage each call) rather than the slower-
// drifting ws_auth cookie. Falls back to the cookie if no token is stored.
function withToken(url: string): string {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

async function request(url: string, init?: RequestInit): Promise<WindowsPayload> {
  const response = await fetch(withToken(url), {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    ...init,
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`terminal-api ${String(response.status)}: ${detail || response.statusText}`);
  }
  const json = (await response.json()) as unknown;
  return WindowListSchema.parse(json);
}

export function useTerminalTabs(
  workspaceId: string,
  enabled: boolean,
): {
  windows: TerminalWindow[];
  isLoading: boolean;
  error: Error | null;
  newWindow: () => void;
  selectWindow: (id: number) => void;
  killWindow: (id: number) => void;
  isMutating: boolean;
} {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['terminal-tabs', workspaceId], [workspaceId]);

  const query = useQuery<WindowsPayload>({
    queryKey,
    queryFn: () => request(tabsUrl(workspaceId)),
    refetchInterval: enabled ? POLL_INTERVAL_MS : false,
    enabled: enabled && workspaceId !== '',
  });

  const mutate = useCallback(
    (path: string, method: 'POST' | 'DELETE') =>
      request(tabsUrl(workspaceId, path), { method }),
    [workspaceId],
  );

  const onSuccess = useCallback(
    (data: WindowsPayload) => {
      queryClient.setQueryData(queryKey, data);
    },
    [queryClient, queryKey],
  );

  const newMutation = useMutation({
    mutationFn: () => mutate('/new', 'POST'),
    onSuccess,
  });
  const selectMutation = useMutation({
    mutationFn: (id: number) => mutate(selectPath(id), 'POST'),
    onSuccess,
  });
  const killMutation = useMutation({
    mutationFn: (id: number) => mutate(killPath(id), 'DELETE'),
    onSuccess,
  });

  return {
    windows: query.data?.windows ?? [],
    isLoading: query.isLoading,
    error: query.error,
    newWindow: () => { newMutation.mutate(); },
    selectWindow: (id: number) => { selectMutation.mutate(id); },
    killWindow: (id: number) => { killMutation.mutate(id); },
    isMutating:
      newMutation.isPending || selectMutation.isPending || killMutation.isPending,
  };
}
